import { App, Plugin, Route } from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import type { HomePortalAppState } from "../app";
import { MagicLinkPage } from "../components/magic-link-page";
import { APP_ROUTES } from "../constants";
import { redeemMagicLink } from "../helpers/redeem-magic-link";

type RefreshLoginSession = () => Promise<boolean>;

export class MagicLinkPlugin implements Plugin<HomePortalAppState> {
  route?: Route;
  private app?: App<HomePortalAppState>;

  constructor(private readonly refreshLoginSession: RefreshLoginSession) {}

  onRegister(app: App<HomePortalAppState>): void {
    this.app = app;

    const view = app.createView({ name: "MagicLink" });
    const page = view.addComponent({ component: MagicLinkPage });
    page.mapState(({ language }) => ({ language }));
    this.route = app.createRoute({
      path: APP_ROUTES.magicLink,
      view,
      isOverlayChild: false,
    });
  }

  async redeemCurrentLink(): Promise<void> {
    if (!this.app) return;

    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      this.app.patchAppState({ isLoading: false });
      this.handleFailure(new Error("magic link token missing"));
      return;
    }

    this.app.patchAppState({ isLoading: true });

    try {
      const { redirectTo } = await redeemMagicLink(token);
      const isLoggedIn = await this.refreshLoginSession();
      if (!isLoggedIn) {
        throw new Error("magic link session refresh failed");
      }

      this.app.navigate(
        redirectTo === APP_ROUTES.admin
          ? APP_ROUTES.admin
          : APP_ROUTES.homePortal,
        { replace: true }
      );
    } catch (error) {
      this.handleFailure(error);
    } finally {
      this.app.patchAppState({ isLoading: false });
    }
  }

  private handleFailure(error: unknown): void {
    if (!this.app) return;
    console.error("Magic link redemption failed:", error);
    this.app.patchAppState({
      snackbar: {
        message: getLocalizedMessage(
          this.app.getAppState().language,
          "magicLink.redeemFailed"
        ),
        severity: "error",
        open: true,
        type: "alert",
      },
    });
    this.app.navigate(APP_ROUTES.home, { replace: true });
  }

  getName(): string {
    return "magic-link-plugin";
  }
}
